/**
 * Keeps the task conversation message list anchored to the latest message.
 * @module useTaskConversationMessageScroll
 */

import { useEffect, useLayoutEffect, useRef } from "react";

/** Pixels from the bottom treated as "already following" the thread. */
const NEAR_BOTTOM_THRESHOLD_PX = 48;

/**
 * Returns whether the scroll container is scrolled near its bottom edge.
 *
 * @param container - Scrollable message list element.
 * @returns True when the user is within {@link NEAR_BOTTOM_THRESHOLD_PX} of the bottom.
 */
const isNearBottom = (container: HTMLElement): boolean =>
  container.scrollHeight - container.scrollTop - container.clientHeight <=
  NEAR_BOTTOM_THRESHOLD_PX;

/** Inputs for {@link useTaskConversationMessageScroll}. */
export interface UseTaskConversationMessageScrollParams {
  taskId: string;
  messagesLoading?: boolean;
  messagesError?: string | null;
  messageCount: number;
}

/**
 * Scrolls the message list to the latest message after load and when the user
 * is already at the bottom; does not jump while the user reads older messages.
 *
 * @param params - Task id, load/error flags, and rendered message count.
 * @returns Ref to attach to the scrollable messages container.
 */
export const useTaskConversationMessageScroll = ({
  taskId,
  messagesLoading = false,
  messagesError = null,
  messageCount,
}: UseTaskConversationMessageScrollParams) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const wasLoadingRef = useRef(messagesLoading);
  const pendingAnchorRef = useRef(true);

  useEffect(() => {
    pendingAnchorRef.current = true;
  }, [taskId]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    const finishedLoading = wasLoadingRef.current && !messagesLoading;

    wasLoadingRef.current = messagesLoading;

    if (!container || messagesLoading || messagesError) {
      return;
    }

    const shouldScrollToBottom =
      pendingAnchorRef.current ||
      finishedLoading ||
      (messageCount > 0 && isNearBottom(container));

    if (!shouldScrollToBottom) {
      return;
    }

    container.scrollTop = container.scrollHeight;
    pendingAnchorRef.current = false;
  }, [taskId, messagesLoading, messagesError, messageCount]);

  return messagesContainerRef;
};
